// Browser-Client des Homelab-Protokolls (docs/protokoll.md, „Phase 2" und
// „Browser-Client"): eine Verbindung /ws/protokoll, Abos mit Muster, Stand je
// Instanz (Snapshot + Delta) und Wiederaufnahme nach Unterbrechung.
//
// API:
//   const aus = abonniere("host.*.stats", { rate: "normal" }, { stand: (i, d, m) => … });
//   aus(); // beim Teardown
//   instanzStand<HostStats>("host.forge.stats")  // reaktiv
//   instanzZeit("host.forge.stats")              // reaktiv, z in ms
//
// Die Rechnung (Delta mischen, b/s-Prüfung, Muster, Ack-Bündelung) steht in
// logik.ts; hier ist nur die Leitung. Lebendigkeit nach dem Vorbild von
// ws-hub.svelte.ts (Backoff, Wache, forceReconnect), das Protokoll läuft
// parallel zum alten /ws.
//
// Lebenszyklus: das erste abonniere() öffnet die Verbindung; sie bleibt offen,
// auch wenn der letzte Abonnent geht (Seitenwechsel soll nicht jedes Mal neu
// anklopfen). stopProtokoll() ist der Aus-Schalter (Tests).

import { SvelteMap } from "svelte/reactivity";

import {
  AckPuffer,
  ausSnapshot,
  darfNeuladen,
  deltaWeg,
  hoechsteRate,
  IdKarte,
  nimmEreignis,
  nimmLuecke,
  passtMuster,
  standFuer,
  wartezeit,
  wendeDeltaAn,
  type Daten,
  type Meta,
  type Rate,
  type Zustand,
} from "./logik";
import { dekodiereFolgeTeil } from "./cbor";
import * as SCHEMA from "./schema.gen";
import { Umwandler } from "./umwandlung";

const { SCHEMA_VERSION } = SCHEMA;

export type { Meta, Rate };

export interface Handler {
  /** Snapshot oder Delta: voller, gemischter Stand der Instanz. */
  stand?: (instanz: string, daten: Daten, meta: Meta) => void;
  /** Ein Ereignis (event-Topics). */
  ereignis?: (instanz: string, daten: Daten, meta: Meta) => void;
  /** Ereignisse sind verloren: die Domäne per REST nachladen. */
  luecke?: (instanz: string) => void;
  /** Instanz vom Server entfernt. */
  weg?: (instanz: string) => void;
}

interface Abonnent {
  rate?: Rate;
  h: Handler;
}

interface Instanz {
  art: "state" | "event";
  zustand: Zustand;
}

// Abos je Muster (mehrere Abonnenten zählen mit).
const _abos = new Map<string, Set<Abonnent>>();
// Stand je Instanzname; überlebt die Verbindung (Grundlage der Wiederaufnahme).
const _instanzen = new Map<string, Instanz>();
// Reaktive Sicht für Komponenten.
const _daten = new SvelteMap<string, Daten>();
const _zeit = new SvelteMap<string, number>();
// Kennung → Instanzname. Gilt nur für EINE Verbindung, wird bei jedem
// Neuaufbau geleert.
const _ids = new IdKarte();
// Instanzen, für die auf dieser Verbindung schon ein Snapshot angefordert ist
// (Delta ohne bekannten Stand), damit nicht jedes Delta eine Anforderung auslöst.
const _angefordert = new Set<number>();
const _acks = new AckPuffer(50);
let _ackTimer: ReturnType<typeof setTimeout> | undefined;

let _ws: WebSocket | undefined;
let _wollen = false;
let _fehlversuche = 0;
let _wiederholen: ReturnType<typeof setTimeout> | undefined;
let _letzteNachricht = 0;
let _verborgenSeit = 0;
let _wache: ReturnType<typeof setInterval> | undefined;
let _beobachtet = false;
let _defektAm = 0;

const STILLE_MAX_MS = 60_000;
const WACHE_MS = 5_000;
const VERBORGEN_NEU_MS = 30_000;
const ACK_MAX_MS = 1_000;
const DEFEKT_PAUSE_MS = 10_000;
const NEULADEN_KEY = "protokoll:neuladen";

// ── Reaktive Sicht ────────────────────────────────────────────────

/** Aktueller Stand einer Instanz (`host.forge.stats`); undefined, solange keiner da ist. */
export function instanzStand<T = Daten>(instanz: string): T | undefined {
  return _daten.get(instanz) as T | undefined;
}

/** Zeit (ms) der letzten Meldung der Quelle für die Instanz. */
export function instanzZeit(instanz: string): number | undefined {
  return _zeit.get(instanz);
}

// ── Abonnieren ────────────────────────────────────────────────────

/**
 * Muster abonnieren (`*` = ein Segment). Mehrere Abonnenten je Muster zählen;
 * der letzte, der geht, löst `abbestellen` aus. Gibt die Abmelde-Funktion zurück.
 */
export function abonniere(muster: string, opts: { rate?: Rate }, h: Handler): () => void {
  const ich: Abonnent = { rate: opts.rate, h };
  let satz = _abos.get(muster);
  const neu = !satz;
  if (!satz) {
    satz = new Set();
    _abos.set(muster, satz);
  }
  const rateVorher = neu ? undefined : ratevon(satz);
  satz.add(ich);

  if (!_wollen) {
    _wollen = true;
    beobachte();
    starteWache();
    verbinde();
  } else if (neu || ratevon(satz) !== rateVorher) {
    sendeAbo(muster, false);
  }

  // Wer später dazukommt, bekommt den schon bekannten Stand sofort.
  if (h.stand) {
    for (const [name, inst] of _instanzen) {
      if (inst.art === "state" && passtMuster(muster, name) && _daten.has(name)) {
        aufrufen(() => h.stand!(name, inst.zustand.daten, meta(inst.zustand)));
      }
    }
  }

  return () => {
    const s = _abos.get(muster);
    const vorher = ratevon(s ?? new Set());
    if (!s || !s.delete(ich)) return;
    if (s.size === 0) {
      _abos.delete(muster);
      senden({ t: "abbestellen", muster });
      verwerfeUnabonniertes();
    } else if (ratevon(s) !== vorher) {
      sendeAbo(muster, false);
    }
  };
}

function ratevon(satz: Set<Abonnent>): Rate | undefined {
  return hoechsteRate([...satz].map((a) => a.rate));
}

function sendeAbo(muster: string, mitStand: boolean): void {
  const satz = _abos.get(muster);
  if (!satz) return;
  const nachricht: Record<string, unknown> = { t: "abo", muster };
  const rate = ratevon(satz);
  if (rate) nachricht.rate = rate;
  // Stand nur beim Neuaufbau: sonst antwortet der Server mit frischen Snapshots.
  if (mitStand) {
    const stand = standFuer(muster, [..._instanzen].map(([n, i]) => [n, i.zustand] as const));
    if (Object.keys(stand).length > 0) nachricht.stand = stand;
  }
  senden(nachricht);
}

/** Instanzen, die zu keinem Muster mehr passen, vergessen (Stand wäre sonst
 *  veraltet; ein späteres Abo holt einen frischen Snapshot). */
function verwerfeUnabonniertes(): void {
  for (const name of [..._instanzen.keys()]) {
    if (!passtIrgendeins(name)) vergiss(name);
  }
}

function vergiss(name: string): void {
  _instanzen.delete(name);
  _daten.delete(name);
  _zeit.delete(name);
  _ids.vergiss(name);
}

function passtIrgendeins(name: string): boolean {
  for (const muster of _abos.keys()) if (passtMuster(muster, name)) return true;
  return false;
}

function handlerFuer(name: string): Handler[] {
  const aus: Handler[] = [];
  for (const [muster, satz] of _abos) {
    if (passtMuster(muster, name)) for (const a of satz) aus.push(a.h);
  }
  return aus;
}

function aufrufen(f: () => void): void {
  try {
    f();
  } catch (e) {
    // Handler-Fehler darf andere Handler und die Leitung nicht treffen.
    console.warn("[protokoll] Handler-Fehler", e);
  }
}

const meta = (z: Zustand): Meta => ({ e: z.e, s: z.s, z: z.z });

// ── Nachrichten ───────────────────────────────────────────────────

function senden(nachricht: Record<string, unknown>): void {
  const ws = _ws;
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  try {
    ws.send(JSON.stringify(nachricht));
  } catch {
    // Die Leitung ist gleich tot -- onclose baut neu auf.
  }
}

function sichtbar(): boolean {
  return document.visibilityState === "visible";
}

function beiOffen(): void {
  _fehlversuche = 0;
  _letzteNachricht = Date.now();
  // IDs gelten nur je Verbindung: alles neu zuordnen lassen.
  _ids.leeren();
  _angefordert.clear();
  _acks.nimm();
  for (const inst of _instanzen.values()) inst.zustand = { ...inst.zustand, wartet: false };
  // Binär anfragen; antwortet der Server mit format "json" (älterer Server),
  // kommen alle Nachrichten als Text -- beiRahmen unterscheidet je Rahmen.
  senden({ t: "hallo", schema: SCHEMA_VERSION, client: "web", format: "cbor" });
  for (const muster of _abos.keys()) sendeAbo(muster, true);
  senden({ t: "sicht", sichtbar: sichtbar() });
}

const _umwandler = new Umwandler(SCHEMA, passtMuster);

/** Ein WS-Rahmen: Text = JSON-Nachricht, Binär = CBOR-Folge von Datensätzen
 *  (docs/protokoll.md, „Phase 3"). Beides läuft durch dieselbe Verarbeitung. */
function beiRahmen(roh: unknown): void {
  _letzteNachricht = Date.now();
  if (typeof roh === "string") {
    let m: unknown;
    try {
      m = JSON.parse(roh);
    } catch {
      return;
    }
    beiNachricht(m);
    return;
  }
  if (!(roh instanceof ArrayBuffer)) return;
  // Alles VOR einer kaputten Stelle wird verarbeitet, danach nichts mehr. Der
  // Server hat „gesendet bis" für den ganzen Rahmen schon erhöht: ein stilles
  // Verwerfen ließe mitgebündelte Ereignisse verloren gehen. Darum die Leitung
  // neu aufbauen -- die Wiederaufnahme mit dem letzten verarbeiteten s holt
  // alles Fehlende nach.
  const { items, fehler } = dekodiereFolgeTeil(roh);
  const aufloesen = (id: number, topic: string | undefined) => _ids.ordneZu({ id, topic });
  for (const satz of items) {
    let m: ReturnType<Umwandler["wandle"]>;
    try {
      m = _umwandler.wandle(satz, aufloesen);
    } catch (e) {
      leitungDefekt(e);
      return;
    }
    if (m) beiNachricht(m);
  }
  if (fehler) leitungDefekt(fehler);
}

/** Unlesbarer Rahmen: Leitung hart neu aufbauen. Kommt das kurz hintereinander
 *  wieder (Fehler beim Sender), mit Pause -- keine enge Schleife. */
function leitungDefekt(e: unknown): void {
  console.warn("[protokoll] Binärrahmen unlesbar, Verbindung wird neu aufgebaut:", e instanceof Error ? e.message : e);
  const jetzt = Date.now();
  const schnell = jetzt - _defektAm < DEFEKT_PAUSE_MS;
  _defektAm = jetzt;
  neuAufbauen(schnell);
}

function beiNachricht(m: unknown): void {
  if (!m || typeof m !== "object") return;
  const n = m as Record<string, any>;
  switch (n.t) {
    case "hallo":
      beiHallo(n.schema);
      break;
    case "snapshot":
      beiSnapshot(n);
      break;
    case "delta":
      beiDelta(n);
      break;
    case "ereignis":
      beiEreignis(n);
      break;
    case "luecke":
      beiLuecke(n);
      break;
    case "weg":
      beiWeg(n);
      break;
    case "ereignis_unbekannt":
      // Ereignis einer Familie, die diese App nicht kennt: nur quittieren.
      merkeAck(n.id, n.s);
      break;
    case "fehler":
      console.warn("[protokoll] Fehler vom Server", n.code, n.text);
      break;
    // puls, befehl_ergebnis: nur Lebenszeichen (Befehle sind noch nicht angebunden).
  }
}

/** Andere Schema-Version: die App ist veraltet -- einmal neu laden, höchstens
 *  alle 10 min (sonst Schleife, falls Server und ausgelieferte App auseinanderliegen). */
function beiHallo(schema: unknown): void {
  if (schema === SCHEMA_VERSION) return;
  let letzter: string | null = null;
  try {
    letzter = sessionStorage.getItem(NEULADEN_KEY);
  } catch {
    /* ohne sessionStorage: wie „noch nie" behandeln */
  }
  if (!darfNeuladen(letzter, Date.now())) {
    console.warn("[protokoll] Schema-Version weicht ab, Neuladen gesperrt", schema, SCHEMA_VERSION);
    return;
  }
  try {
    sessionStorage.setItem(NEULADEN_KEY, String(Date.now()));
  } catch {
    /* s. o. */
  }
  location.reload();
}

/** Die erste Nachricht je Instanz trägt `topic`: Zuordnung id → Name setzen. */
function ordneZu(m: Record<string, any>): string | undefined {
  return _ids.ordneZu(m as { id: number; topic?: string });
}

function fordereSnapshot(id: number): void {
  senden({ t: "snapshot", id });
}

function setzeStand(name: string, z: Zustand, datenGeaendert: boolean): void {
  if (datenGeaendert || !_daten.has(name)) _daten.set(name, z.daten);
  _zeit.set(name, z.z);
}

function beiSnapshot(m: Record<string, any>): void {
  const name = ordneZu(m);
  if (!name || !passtIrgendeins(name)) return;
  _angefordert.delete(m.id);
  const zustand = ausSnapshot(m as { e: string; s: number; z: number; d: Daten });
  _instanzen.set(name, { art: "state", zustand });
  setzeStand(name, zustand, true);
  melde(name, zustand);
}

function beiDelta(m: Record<string, any>): void {
  const name = ordneZu(m);
  // Unbekannte Kennung ohne `topic`: Nachzügler einer abbestellten Instanz,
  // still verwerfen (eine Snapshot-Anfrage wäre für den Server ein Fehler).
  if (!name || !passtIrgendeins(name)) return;
  const inst = _instanzen.get(name);
  if (deltaWeg(name, inst?.art === "state") === "snapshot" || !inst) {
    // Wiederaufnahme ohne Stand bei uns (z. B. nach dem Vergessen): Snapshot.
    if (!_angefordert.has(m.id)) {
      _angefordert.add(m.id);
      fordereSnapshot(m.id);
    }
    return;
  }
  const folge = wendeDeltaAn(inst.zustand, m as { b: number; s: number; z: number; d: Daten; e?: string });
  if (folge.snapshotAnfordern) fordereSnapshot(m.id);
  if (folge.neu === inst.zustand) return;
  inst.zustand = folge.neu;
  if (folge.snapshotAnfordern) return; // verworfen, nur `wartet` gesetzt
  setzeStand(name, folge.neu, folge.datenGeaendert);
  melde(name, folge.neu);
}

function melde(name: string, z: Zustand): void {
  for (const h of handlerFuer(name)) {
    if (h.stand) aufrufen(() => h.stand!(name, z.daten, meta(z)));
  }
}

function beiEreignis(m: Record<string, any>): void {
  const name = ordneZu(m);
  if (!name || !passtIrgendeins(name)) return;
  const alt = _instanzen.get(name);
  const folge = nimmEreignis(alt?.art === "event" ? alt.zustand : undefined, m as { e?: string; s: number; z: number });
  _instanzen.set(name, { art: "event", zustand: folge.neu });
  merkeAck(m.id, m.s);
  if (!folge.zustellen) return;
  const d = (m.d ?? {}) as Daten;
  for (const h of handlerFuer(name)) {
    if (h.ereignis) aufrufen(() => h.ereignis!(name, d, meta(folge.neu)));
  }
}

function beiLuecke(m: Record<string, any>): void {
  const name = ordneZu(m);
  if (!name || !passtIrgendeins(name)) return;
  const alt = _instanzen.get(name);
  _instanzen.set(name, { art: "event", zustand: nimmLuecke(alt?.art === "event" ? alt.zustand : undefined, m as { e?: string; s: number }) });
  merkeAck(m.id, m.s);
  for (const h of handlerFuer(name)) {
    if (h.luecke) aufrufen(() => h.luecke!(name));
  }
}

// `weg` mit topic: nach einer Wiederaufnahme für eine Instanz, die während der
// Unterbrechung entfernt wurde (die id kannte diese Verbindung noch nicht).
function beiWeg(m: Record<string, any>): void {
  const name = ordneZu(m);
  if (!name) return;
  const handler = handlerFuer(name);
  vergiss(name);
  for (const h of handler) {
    if (h.weg) aufrufen(() => h.weg!(name));
  }
}

// ── Bestätigungen (gebündelt) ─────────────────────────────────────

function merkeAck(id: number, s: number): void {
  if (_acks.merke(id, s)) {
    sendeAcks();
  } else if (!_ackTimer) {
    _ackTimer = setTimeout(sendeAcks, ACK_MAX_MS);
  }
}

function sendeAcks(): void {
  if (_ackTimer) {
    clearTimeout(_ackTimer);
    _ackTimer = undefined;
  }
  for (const a of _acks.nimm()) senden({ t: "ack", id: a.id, s: a.s });
}

// ── Leitung ───────────────────────────────────────────────────────

function wsUrl(): string {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}/ws/protokoll`;
}

function verbinde(): void {
  if (_ws && _ws.readyState !== WebSocket.CLOSED) return;
  const ws = new WebSocket(wsUrl());
  ws.binaryType = "arraybuffer"; // CBOR-Rahmen als ArrayBuffer statt Blob
  _ws = ws;
  ws.onopen = beiOffen;
  ws.onmessage = (e) => beiRahmen(e.data);
  ws.onclose = () => {
    _ws = undefined;
    if (_ackTimer) {
      clearTimeout(_ackTimer);
      _ackTimer = undefined;
    }
    if (_wollen) {
      _fehlversuche++;
      _wiederholen = setTimeout(verbinde, wartezeit(_fehlversuche));
    }
  };
  ws.onerror = () => {
    // onclose folgt direkt und baut neu auf.
  };
}

/** Tote oder zweifelhafte Leitung hart abklemmen und sofort neu aufbauen. */
function neuAufbauen(mitPause = false): void {
  if (!_wollen) return;
  _fehlversuche = 0;
  if (_wiederholen) {
    clearTimeout(_wiederholen);
    _wiederholen = undefined;
  }
  if (_ws) {
    const alt = _ws;
    _ws = undefined;
    alt.onopen = null;
    alt.onmessage = null;
    alt.onclose = null;
    alt.onerror = null;
    try {
      alt.close();
    } catch {
      /* ignorieren */
    }
  }
  if (mitPause) _wiederholen = setTimeout(verbinde, DEFEKT_PAUSE_MS / 2);
  else verbinde();
}

// Wache für den SICHTBAREN Tab: 60 s ohne jede Nachricht (der Server pulst
// spätestens alle 25 s) → halb offene Leitung, neu aufbauen. Ist die Leitung
// zu, wartet schon der Backoff aus onclose. Im Hintergrund schweigt sie.
function starteWache(): void {
  if (_wache) return;
  _wache = setInterval(() => {
    if (!_wollen || !sichtbar()) return;
    if (!_ws || _ws.readyState !== WebSocket.OPEN) return;
    if (Date.now() - _letzteNachricht >= STILLE_MAX_MS) neuAufbauen();
  }, WACHE_MS);
}

function beiSichtbarkeit(): void {
  if (!_wollen) return;
  if (!sichtbar()) {
    _verborgenSeit = Date.now();
    senden({ t: "sicht", sichtbar: false });
    return;
  }
  const lang = _verborgenSeit > 0 && Date.now() - _verborgenSeit > VERBORGEN_NEU_MS;
  _verborgenSeit = 0;
  if (lang) neuAufbauen(); // beiOffen meldet `sicht` und holt den Stand nach
  else senden({ t: "sicht", sichtbar: true });
}

function beiOnline(): void {
  neuAufbauen();
}

function beobachte(): void {
  if (_beobachtet) return;
  _beobachtet = true;
  document.addEventListener("visibilitychange", beiSichtbarkeit);
  window.addEventListener("online", beiOnline);
}

/** Alles abbauen und vergessen (Tests, Abmeldung). */
export function stopProtokoll(): void {
  _wollen = false;
  if (_wache) {
    clearInterval(_wache);
    _wache = undefined;
  }
  if (_wiederholen) {
    clearTimeout(_wiederholen);
    _wiederholen = undefined;
  }
  if (_ackTimer) {
    clearTimeout(_ackTimer);
    _ackTimer = undefined;
  }
  if (_beobachtet) {
    document.removeEventListener("visibilitychange", beiSichtbarkeit);
    window.removeEventListener("online", beiOnline);
    _beobachtet = false;
  }
  if (_ws) {
    const alt = _ws;
    _ws = undefined;
    alt.onclose = null;
    try {
      alt.close();
    } catch {
      /* ignorieren */
    }
  }
  _abos.clear();
  _instanzen.clear();
  _daten.clear();
  _zeit.clear();
  _ids.leeren();
  _angefordert.clear();
  _acks.nimm();
  _fehlversuche = 0;
  _verborgenSeit = 0;
  _defektAm = 0;
}
